import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EMAIL_EVENTS, SENT_STATUSES, SMS_ONLY_EVENTS } from '@app/shared';
import type { SentNotificationDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { StatusChip } from '../../components/StatusChip';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useFeatureReleased } from '../../release';
import { useCustomerContext } from './CustomerLayout';
import { Section } from './parts';

const EVENTS = [...EMAIL_EVENTS, ...SMS_ONLY_EVENTS];

/** The first day of the reference's window: the most recent 30 days, today included (UTC). */
const windowStart = (now = new Date()): string =>
  new Date(now.getTime() - 29 * 86_400_000).toISOString().slice(0, 10);
const CHANNEL = { email: 'EMAIL', sms: 'SMS', ivr: 'IVR' } as const;

/**
 * Resend Notification (PC930): the messages sent to this customer, with the reference's
 * filters (date range, notification type, event type, sent status), "View Message", and
 * sending a message again. The reference's columns were not read.
 */
export function SentNotificationsPage() {
  const { customer } = useCustomerContext();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const list = useListState({ dateFrom: '', dateTo: '', channel: '', eventName: '', status: '' });
  const [viewing, setViewing] = useState<SentNotificationDto | null>(null);
  const featureOn = useFeatureReleased();
  const lastThirtyDays = featureOn('resend-30-days');
  // The list is not paged: only the filters go to the API.
  const { page: _page, pageSize: _size, sort: _sort, ...chosen } = list.params;
  // With the reference's window on, nothing older than 30 days is asked for.
  const start = windowStart();
  const filters = lastThirtyDays
    ? { ...chosen, dateFrom: chosen.dateFrom && chosen.dateFrom > start ? chosen.dateFrom : start }
    : chosen;
  const sent = useQuery({
    queryKey: ['sent-notifications', customer.id, filters],
    queryFn: () =>
      api.get<{ data: SentNotificationDto[] }>(`/sent-notifications/${customer.id}`, filters),
    select: (r) => r.data,
  });
  const resend = useMutation({
    mutationFn: (id: string) =>
      api.post<{ status: string }>(`/sent-notifications/${customer.id}/${id}/resend`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sent-notifications'] }),
  });

  const columns: Column<SentNotificationDto>[] = [
    { key: 'sentAt', header: 'Sent Date', nowrap: true, render: (n) => formatDateTime(n.sentAt) },
    { key: 'channel', header: 'Notification Type', render: (n) => CHANNEL[n.channel] },
    { key: 'eventName', header: 'Event Type', render: (n) => n.eventName },
    { key: 'to', header: 'Sent To', render: (n) => n.to },
    { key: 'status', header: 'Sent Status', render: (n) => <StatusChip status={n.status} /> },
    {
      key: 'action',
      header: 'Action',
      render: (n) => (
        <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
          <Button size="small" onClick={() => setViewing(n)}>
            View Message
          </Button>
          <Button
            size="small"
            disabled={resend.isPending}
            aria-label={`Resend ${n.eventName} to ${n.to}`}
            onClick={() =>
              void resend
                .mutateAsync(n.id)
                .then((done) =>
                  done.status === 'Success'
                    ? notify.success('Notification sent again.')
                    : notify.error(new Error('The notification could not be delivered.')),
                )
                .catch((err: unknown) => notify.error(err))
            }
          >
            Resend
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Section title="Resend Notification">
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        {(['dateFrom', 'dateTo'] as const).map((key) => (
          <TextField
            key={key}
            type="date"
            label={key === 'dateFrom' ? 'Date Range From' : 'Date Range To'}
            value={list.draft[key]}
            onChange={(e) => list.setField(key, e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        ))}
        <TextField
          select
          label="Notification Type"
          value={list.draft.channel}
          onChange={(e) => list.setField('channel', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="sms">SMS</MenuItem>
          <MenuItem value="email">EMAIL</MenuItem>
        </TextField>
        <TextField
          select
          label="Event Type"
          value={list.draft.eventName}
          onChange={(e) => list.setField('eventName', e.target.value)}
          slotProps={{
            select: { MenuProps: { slotProps: { paper: { sx: { maxHeight: 320 } } } } },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {EVENTS.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sent Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {SENT_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>
      {lastThirtyDays ? (
        <Typography color="text.secondary" sx={{ fontSize: 13, px: 1.5, pb: 1 }}>
          **Data covering the most recent 30 days is accessible.
        </Typography>
      ) : null}
      <DataTable
        label="Sent notifications"
        columns={columns}
        rows={sent.data}
        rowKey={(n) => n.id}
        loading={sent.isFetching}
        error={sent.error}
        onRetry={() => void sent.refetch()}
        emptyText="No notification was sent to this customer."
      />
      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>View Message</DialogTitle>
        <DialogContent dividers>
          {viewing ? (
            <>
              <Typography sx={{ fontWeight: 600 }}>
                {viewing.eventName} to {viewing.to}
              </Typography>
              {viewing.subject ? <Typography sx={{ mt: 1 }}>{viewing.subject}</Typography> : null}
              <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{viewing.message}</Typography>
              {viewing.error ? (
                <Typography color="error" sx={{ mt: 1 }}>
                  Not delivered: {viewing.error}
                </Typography>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}
